import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { publicApi, publicFileUrl } from "@/lib/publicApi";
import { api } from "@/lib/api";
import {
  Ruler, House, Bathtub, Stairs,
  Envelope, Phone, CaretLeft, CaretRight, X,
  WhatsappLogo, Check, CalendarBlank, Spinner, CheckCircle,
  Lightning, Thermometer, Car, Tree, Elevator, Sun, Couch, Calendar,
  CurrencyEur, Wrench,
} from "@phosphor-icons/react";
import PublicListingV2 from "@/components/publicListing/PublicListingV2";

function formatPrice(p) {
  if (!p || Number(p) < 1) return null;
  return "€ " + Number(p).toLocaleString("it-IT");
}

/**
 * PublicListing — iter v28 r26
 * Stile "Elegante" reso come VERA BROCHURE STAMPATA:
 *   • Ogni sezione = pagina di volantino (carta avorio, ombra, numero pagina).
 *   • Mobile-first: padding compatti, hero 70vh, font ricalibrati.
 *   • Ordine: Cover → Galleria → Descrizione → Prima/Dopo → Scheda → Contatti.
 *   • Galleria stile brochure: cinema-strip auto-scroll + mosaico magazine.
 */
export default function PublicListing() {
  const { publicId } = useParams();
  const location = useLocation();
  // iter v28 r440 — User M1353: anteprima pre-pubblicazione per l'agente.
  // Quando la route è `/listing-preview/{pid}`, chiamiamo l'endpoint
  // autenticato `/api/properties/{pid}/preview-listing` invece dell'endpoint
  // pubblico `/api/public/listing/{public_id}`. Owner-only.
  const isPreview = location.pathname.startsWith("/listing-preview/");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (isPreview) {
          // Owner preview: fetch authed by property_id
          const { data } = await api.get(`/properties/${publicId}/preview-listing`);
          setData(data);
        } else {
          const { data } = await publicApi.get(`/listing/${publicId}`);
          setData(data);
        }
      } catch (e) {
        setData({ error: e?.response?.data?.detail || "Non disponibile" });
      }
      setLoading(false);
      if (!isPreview && !tracked.current) {
        tracked.current = true;
        publicApi.post(`/listing/${publicId}/view`).catch(() => {});
      }
    })();
  }, [publicId, isPreview]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF7] gap-4">
        {/* iter v28 r97 — Spinner col logo Pronto al posto della pagina
            giallina con "Pronto" generico. La P ruota lievemente per dare
            feedback di caricamento (richiesta utente: "la P col cerchio
            che gira, solito che mettiamo quando ci sono dei caricamenti"). */}
        <div className="relative" style={{ width: 68, height: 68 }}>
          {/* Cerchio che gira intorno al logo */}
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{
              border: "2px solid #EAE7DF",
              borderTopColor: "#7C8B6E",
              animation: "vista-spin 0.9s linear infinite",
            }}
          />
          {/* Logo Pronto centrato */}
          <img
            src="/logo/mark-symbol.png"
            alt="Pronto"
            width={36}
            height={36}
            className="absolute"
            style={{
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              objectFit: "contain",
              animation: "vista-pulse 1.8s ease-in-out infinite",
            }}
            draggable="false"
          />
        </div>
        <div className="font-brand text-[15px]" style={{ color: "#7C8B6E" }}>Pronto</div>
        <style>{`
          @keyframes vista-spin { to { transform: rotate(360deg); } }
          @keyframes vista-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
        `}</style>
      </div>
    );
  }
  if (data?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EFE8D9] p-6">
        <div className="card-flat p-10 text-center max-w-md">
          <h1 className="font-editorial text-3xl text-[#7C8B6E]">Annuncio non disponibile</h1>
          <p className="mt-3 text-[#6B6A65]">{data.error}</p>
          <Link to="/" className="btn-primary mt-8 inline-flex">Torna a Pronto</Link>
        </div>
      </div>
    );
  }

  const { property: p, photos: rawPhotos = [], agency = {}, listing = {} } = data || {};

  // iter v28 r28 — Separa foto reali da planimetrie.
  const allPhotos = rawPhotos.filter((ph) => (ph.kind || "photo") !== "floor_plan");
  const allPlans = rawPhotos.filter((ph) => (ph.kind || "photo") === "floor_plan");

  // iter v28 r29 — Toggle UNIFICATO "Mostra Prima · Dopo" (default true)
  const showBeforeAfter = p?.show_before_after_section !== false;
  const beforeAfters = showBeforeAfter
    ? allPhotos.filter((ph) => ph.staged_url && ph.original_url)
    : [];

  // iter v28 r29 — Costruisci la galleria pagina online.
  // Quando il toggle è ON: per ogni foto modificata, affianca la "Prima"
  // SUBITO DOPO la sua versione DOPO, creando una sequenza
  // dopo→prima→dopo→prima. La cover (photos[0]) resta sempre DOPO.
  // Quando OFF: solo foto modificate (versione DOPO).
  const photos = (() => {
    if (!showBeforeAfter) return allPhotos;
    const seq = [];
    for (const ph of allPhotos) {
      seq.push(ph);
      if (ph.staged_url && ph.original_url) {
        seq.push({
          ...ph,
          id: `${ph.id}__before`,
          url: ph.original_url,
          // Marker per pillola "Prima" sull'immagine
          _is_before: true,
          // Rimuoviamo gli staged/original per evitare che venga interpretata
          // come ulteriore Prima/Dopo nella sezione interattiva.
          staged_url: undefined,
          original_url: undefined,
        });
      }
    }
    return seq;
  })();

  // Planimetrie: separa coppie (vuota+arredata) dalle singole.
  const planPairs = allPlans.filter((ph) => ph.staged_url && ph.original_url);
  const planSingles = allPlans.filter((ph) => !(ph.staged_url && ph.original_url));

  const showProntoBranding = (agency?.plan || "free") !== "pro";
  const brandColor = agency?.brand_color || "#7C8B6E";

  // ── Conta sezioni effettive per numero pagina dinamico ────────────────
  // iter v28 r36 — Nuovo ordine: Cover → Descrizione → Galleria → Prima/Dopo
  // → Planimetria → Scheda → Contatti.
  const pages = ["cover"];
  if (p?.ai_description) pages.push("desc");
  if (photos.length > 0) pages.push("gallery");
  if (beforeAfters.length > 0) pages.push("transform");
  if (allPlans.length > 0) pages.push("plan");
  pages.push("specs");
  pages.push("contact");
  const totalPages = pages.length;
  const pageIdx = (key) => String(pages.indexOf(key) + 1).padStart(2, "0");

  return (
    <div className="min-h-screen" style={{ background: ((listing?.style || "elegante") === "luxury") ? "#0B101E" : "#FAFAF7" }}>
      {/* iter v28 r50 — PublicListingV2: i 4 stili "super figi" completamente
          ridisegnati. Usa il vecchio brochure-stage solo come fallback se
          MAI uno stile non valido viene salvato (paranoia).
          iter v28 r126 — Accetta querystring ?style= per preview rapida dei
          4 layout senza salvare nel DB (testing/anteprima). */}
      <PublicListingV2
        data={{ p, agency, photos, listing, publicId, listingActive: !!listing }}
        style={new URLSearchParams(window.location.search).get("style") || undefined}
        onBook={() => setBookingOpen(true)}
        onContact={() => setBookingOpen(true)}
        onPhoto={(idx) => setLightbox(idx)}
      />
      {false && (
      <main className="brochure-stage" data-style={listing?.style || "elegante"}>

      {true && (<>
        {/* ╭───────────────── PAGINA 01 — COPERTINA per stile ─────────────────╮
            iter v28 r44 — Layout DIVERSI per ogni stile (richiesta utente).
            Non più solo font/colori: vera diversità strutturale.
              • Elegante: foto blended brand color + titolo italic centrato
              • Luxury: foto incorniciata + cornice doppia + serif italic top
              • Moderno: foto top-half + pannello brand bottom-half
              • Minimal: foto laterale destra full-bleed + colonna sinistra      */}
        <CoverSection
          variant={listing?.style || "elegante"}
          p={p}
          agency={agency}
          photos={photos}
          publicId={publicId}
          brandColor={brandColor}
          pageIdx={pageIdx}
          totalPages={totalPages}
        />

        {/* ╭─────────── SCHEDA IMMOBILIARE.IT-STYLE (sotto cover) ─────────╮
            iter v28 r49 — Sotto la cover artistica, una scheda compatta
            stile Immobiliare.it: photo slider con thumbs + prezzo grande +
            logo agenzia + titolo + indirizzo + specs in chip. Single-card
            modern, niente più look "carta funeraria".                       */}
        <ListingSummaryCard
          p={p}
          agency={agency}
          photos={photos}
          publicId={publicId}
          brandColor={brandColor}
          onContactClick={() => setBookingOpen(true)}
          onGalleryClick={() => setLightbox(0)}
        />

        {false && (
        <section
          className="brochure-page brochure-frame relative overflow-hidden text-white"
          style={{ padding: 0, background: brandColor }}
          data-testid="public-hero-section-legacy"
        >
          <span className="brochure-pagenum z-20 text-white/70" style={{color:"rgba(255,255,255,0.7)"}}>
            {pageIdx("cover")} / {String(totalPages).padStart(2,"0")}
          </span>

          <div className="relative min-h-[88vh] sm:min-h-[92vh] flex flex-col justify-between p-6 sm:p-10 lg:p-14">
            {/* Foto con auto-slideshow + tint brand multiply */}
            <CoverSlideshow photos={photos.slice(0, 8)} publicId={publicId} brandColor={brandColor} />

            {/* Doppio frame interno bianco */}
            <div className="pointer-events-none absolute inset-[14px] sm:inset-5 border border-white/30 z-[1]" />

            {/* HEADER — Logo agenzia in alto al centro.
                iter v28 r35 — Solo logo monocromatico viene invertito su sfondo
                scuro. Policromo resta a colori naturali (mai pill di sfondo). */}
            <div className="relative z-10 text-center pt-4 sm:pt-6">
              {agency?.logo_url ? (
                <img
                  src={agency.logo_url}
                  alt={agency.name || "Agenzia"}
                  className="h-12 sm:h-16 w-auto max-w-[260px] mx-auto object-contain"
                  style={agency.logo_is_monochrome !== false ? { filter: "brightness(0) invert(1)" } : undefined}
                  data-testid="public-brand-logo"
                />
              ) : agency?.name ? (
                <div className="font-editorial italic text-2xl sm:text-3xl tracking-[0.05em] uppercase" data-testid="public-brand">
                  {agency.name}
                </div>
              ) : null}
              {agency?.website && (
                <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.28em] opacity-75">
                  {agency.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </div>
              )}
              {/* Linea decorativa sotto il logo */}
              <div className="mt-3 sm:mt-4 w-12 h-px bg-white/45 mx-auto" />
            </div>

            {/* BODY centrato — titolo, città, dati salienti */}
            <div className="relative z-10 text-center max-w-2xl mx-auto -mt-4 sm:-mt-8">
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-semibold opacity-90 mb-4 sm:mb-5" data-testid="public-type">
                {p?.listing_type === "affitto" ? "In affitto" : "Esclusiva"}{p?.property_type ? ` · ${p.property_type}` : ""}
              </div>
              <h1
                className="font-editorial italic text-[clamp(34px,8vw,68px)] leading-[1.04] tracking-tight"
                data-testid="public-title"
              >
                {p?.title}
              </h1>
              {(p?.city || p?.address) && (
                <div className="mt-3 sm:mt-4 font-editorial italic text-base sm:text-lg opacity-90">
                  {p.address ? `${p.address} · ` : ""}{p.city}
                </div>
              )}
            </div>

            {/* FOOTER della cover — prezzo grande + dati + agente */}
            <div className="relative z-10 text-center">
              <div className="font-editorial text-[clamp(28px,6vw,46px)] leading-none" data-testid="public-price">
                {formatPrice(p?.price) || "Trattativa riservata"}
              </div>
              <div className="mt-2 sm:mt-3 flex items-center justify-center gap-x-3 gap-y-1 flex-wrap text-[10px] sm:text-[11px] uppercase tracking-[0.24em] font-semibold opacity-90">
                {p?.surface && <span>{p.surface} mq</span>}
                {p?.rooms && <><span className="opacity-50">·</span><span>{p.rooms} loc.</span></>}
                {p?.bathrooms && <><span className="opacity-50">·</span><span>{p.bathrooms} bagni</span></>}
                {p?.energy_class && <><span className="opacity-50">·</span><span>Classe {p.energy_class}</span></>}
              </div>

              {/* Linea + agente */}
              <div className="mt-6 sm:mt-7 w-12 h-px bg-white/45 mx-auto" />
              <div className="mt-3 text-[12px] sm:text-[13px] opacity-90 font-medium">
                {agency.agent_name || agency.name}
                {agency.phone && <span className="opacity-70"> · {agency.phone}</span>}
              </div>
            </div>
          </div>
        </section>
        )}

        {/* ╭───────────────────── PAGINA 02 — DESCRIZIONE ─────────────────────╮
            iter v28 r36 — Spostata in posizione 02 (dopo cover). */}
        {p?.ai_description && (
          <section className="brochure-page brochure-frame" data-testid="public-description-section">
            <BrochurePageHeader agency={agency} />
            <span className="brochure-pagenum">{pageIdx("desc")} / {String(totalPages).padStart(2,"0")}</span>
            <div className="brochure-section-head">
              <div className="brochure-label">L'immobile</div>
              <h2 className="brochure-title">Descrizione</h2>
            </div>
            <div
              className="text-[13px] sm:text-[14px] leading-[1.75] text-[#1C1C1A] font-light max-w-3xl mx-auto first-letter:font-editorial first-letter:italic first-letter:text-[52px] sm:first-letter:text-[64px] first-letter:leading-[0.85] first-letter:mr-2 first-letter:float-left first-letter:text-[#7C8B6E] whitespace-pre-wrap"
              data-testid="public-description"
            >
              {p.ai_description}
            </div>
          </section>
        )}

        {/* ╭──────────────────── PAGINA 03 — GALLERIA collage ────────────────────╮
            iter v28 r36 — Sostituito GallerySlider con GalleryCollage:
            griglia asimmetrica magazine con tutte le foto visibili in piccolo;
            click su una foto apre il lightbox a tutto schermo.                */}
        {photos.length > 0 && (
          <section className="brochure-page brochure-frame" data-testid="public-gallery-section">
            <BrochurePageHeader agency={agency} />
            <span className="brochure-pagenum">{pageIdx("gallery")} / {String(totalPages).padStart(2,"0")}</span>
            <div className="brochure-section-head">
              <div className="brochure-label">La galleria</div>
              <h2 className="brochure-title">Stanza per stanza</h2>
              <p className="text-[12px] text-[#6B6A65] mt-2 max-w-md mx-auto leading-snug font-light">
                Tocca una foto per ingrandirla.
              </p>
            </div>

            <GalleryCollage
              photos={photos}
              publicId={publicId}
              onZoom={(idx) => setLightbox(idx)}
            />
          </section>
        )}

        {/* ╭──────────────────── PAGINA 04 — PRIMA / DOPO ────────────────────╮
            iter v28 r28 — Un solo slider grande + thumbnail selector se >1 */}
        {beforeAfters.length > 0 && (
          <section className="brochure-page brochure-frame" data-testid="public-trasformazioni">
            <BrochurePageHeader agency={agency} />
            <span className="brochure-pagenum">{pageIdx("transform")} / {String(totalPages).padStart(2,"0")}</span>
            <div className="brochure-section-head">
              <div className="brochure-label">Trasformazioni</div>
              <h2 className="brochure-title">Prima · Dopo</h2>
              <p className="text-[12px] text-[#6B6A65] mt-2 max-w-md mx-auto leading-snug font-light">
                {beforeAfters.length > 1
                  ? "Trascina il divisore o scegli un altro ambiente qui sotto."
                  : "Tocca o trascina il divisore. Si muove anche da solo."}
              </p>
            </div>
            <BeforeAfterSelector items={beforeAfters} publicId={publicId} />
          </section>
        )}

        {/* ╭───────────────────── PAGINA 05 — PLANIMETRIA ─────────────────────╮
            iter v28 r28 — Sezione planimetria centrata.
            • Se c'è una coppia (vuota + arredata) → Prima/Dopo slider con
              vista iniziale = vuota (initialPos=95).
            • Piante singole → immagine grande centrata.                       */}
        {allPlans.length > 0 && (
          <section className="brochure-page brochure-frame" data-testid="public-floorplan-section">
            <BrochurePageHeader agency={agency} />
            <span className="brochure-pagenum">{pageIdx("plan")} / {String(totalPages).padStart(2,"0")}</span>
            <div className="brochure-section-head">
              <div className="brochure-label">La pianta</div>
              <h2 className="brochure-title">Planimetria</h2>
              {planPairs.length > 0 && (
                <p className="text-[12px] text-[#6B6A65] mt-2 max-w-md mx-auto leading-snug font-light">
                  Trascina per vedere la pianta arredata.
                </p>
              )}
            </div>
            <PlanimetriaSection
              pairs={planPairs}
              singles={planSingles}
              publicId={publicId}
            />
          </section>
        )}

        {/* ╭─────────────────── PAGINA 06 — SCHEDA + FEATURES ──────────────────╮ */}
        <section className="brochure-page brochure-frame" data-testid="public-specs-section">
          <BrochurePageHeader agency={agency} />
          <span className="brochure-pagenum">{pageIdx("specs")} / {String(totalPages).padStart(2,"0")}</span>
          <div className="brochure-section-head">
            <div className="brochure-label">In dettaglio</div>
            <h2 className="brochure-title">Scheda tecnica</h2>
          </div>
          <PropertyDataSheet p={p} />

          {p?.features?.length > 0 && (
            <div className="mt-8 pt-7 border-t border-[#E8DFCC]" data-testid="public-features-section">
              <div className="text-center mb-5">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#9A8E72] font-semibold">Caratteristiche</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2.5 max-w-2xl mx-auto">
                {p.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-[13px] text-[#1C1C1A]" data-testid={`feature-${f}`}>
                    <Check size={13} weight="bold" className="text-[#7C8B6E] flex-shrink-0" /> {f}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ╭─────────────────────── PAGINA 06 — CONTATTI ───────────────────────╮ */}
        <section className="brochure-page brochure-page--dark brochure-frame" data-testid="public-contact-section">
          <BrochurePageHeader agency={agency} dark />
          <span className="brochure-pagenum" style={{color:"#9A8E72"}}>{pageIdx("contact")} / {String(totalPages).padStart(2,"0")}</span>
          <div className="brochure-section-head">
            <div className="brochure-label">Contatti</div>
            <h2 className="brochure-title">Vieni a visitarla</h2>
          </div>
          {/* iter v28 r28 — Card contatti centrata verticale con avatar grande */}
          <div className="max-w-md mx-auto text-center" data-testid="contact-card">
            {/* Avatar grande */}
            {agency.picture ? (
              <img
                src={agency.picture}
                alt={agency.agent_name || ""}
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mx-auto ring-2 ring-[#9A8E72]/30"
              />
            ) : agency.logo_url ? (
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#FBF8F1] border border-[#9A8E72]/30 flex items-center justify-center overflow-hidden mx-auto">
                <img src={agency.logo_url} alt="" className="max-w-[80%] max-h-[80%] object-contain" />
              </div>
            ) : (
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center font-editorial text-3xl sm:text-4xl mx-auto">
                {(agency.name || agency.agent_name || "?")[0]}
              </div>
            )}

            {/* Nome agente in evidenza */}
            {agency.agent_name && (
              <div className="font-editorial text-2xl sm:text-3xl italic text-[#FBF8F1] leading-tight mt-4">
                {agency.agent_name}
              </div>
            )}
            {agency.name && agency.name !== agency.agent_name && (
              <div className="text-[13px] sm:text-[14px] text-[#C9BFA8] mt-1 uppercase tracking-[0.18em]">
                {agency.name}
              </div>
            )}
            {agency.phone && (
              <div className="text-[13px] text-[#9A8E72] mt-1">{agency.phone}</div>
            )}

            {/* Linea decorativa */}
            <div className="my-5 mx-auto w-12 h-px bg-[#9A8E72]/45" />

            {/* CTA primario grande full-width */}
            <button
              onClick={() => setBookingOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-white text-[14px] font-semibold rounded-full transition-opacity hover:opacity-90 w-full"
              style={{ backgroundColor: brandColor }}
              data-testid="public-book-btn"
            >
              <CalendarBlank size={15} weight="fill" /> Prenota una visita
            </button>

            {/* Secondari riordinati in griglia 3-col centrata */}
            <div className="mt-3 grid grid-cols-3 gap-2">
              {agency.phone && (
                <a
                  href={`https://wa.me/${agency.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Salve, sono interessato all'immobile "${p?.title}"`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 bg-[#25D366] text-white text-[12px] font-semibold rounded-full hover:opacity-90 transition-opacity"
                  data-testid="public-whatsapp-btn"
                >
                  <WhatsappLogo size={13} weight="bold" /> WhatsApp
                </a>
              )}
              {agency.phone && (
                <a
                  href={`tel:${agency.phone}`}
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 bg-white text-[#1C1C1A] text-[12px] font-semibold rounded-full hover:bg-[#FBF8F1] transition-colors"
                  data-testid="public-call-btn"
                >
                  <Phone size={13} weight="bold" /> Chiama
                </a>
              )}
              {agency.email && (
                <a
                  href={`mailto:${agency.email}?subject=${encodeURIComponent(`Richiesta info — ${p?.title}`)}`}
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 bg-transparent text-[#FBF8F1] text-[12px] font-semibold border border-[#9A8E72]/50 rounded-full hover:bg-[#FBF8F1] hover:text-[#1C1C1A] transition-colors"
                  data-testid="public-email-btn"
                >
                  <Envelope size={13} weight="bold" /> Scrivi
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Footer Pronto branding (solo per piano free/base) */}
        {showProntoBranding && (
          <div className="text-center text-[11px] text-[#9A8E72] pt-6 pb-2">
            <a href="https://prontoimmo.it" className="hover:text-[#7C8B6E]">prontoimmo.it</a> · annuncio creato con Pronto
          </div>
        )}
      </>)}
      </main>
      )}{/* end legacy brochure-stage */}

      {/* Lightbox */}
      {lightbox !== null && (
        <LightboxView
          photos={photos}
          startIndex={lightbox}
          publicId={publicId}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Sticky bottom bar (mobile only) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7E0] px-3 py-2 flex items-center gap-2 shadow-[0_-8px_22px_-12px_rgba(0,0,0,0.18)]" data-testid="public-mobile-cta-bar">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold leading-none">{p?.listing_type === "affitto" ? "In affitto" : "In vendita"}</div>
          <div className="font-editorial text-[15px] text-[#1C1C1A] leading-tight">{formatPrice(p?.price) || "Trattativa riservata"}</div>
        </div>
        <button
          onClick={() => setBookingOpen(true)}
          className="inline-flex items-center gap-1.5 text-white text-[12.5px] font-semibold px-3.5 py-2 rounded-md transition-opacity hover:opacity-90"
          style={{ backgroundColor: brandColor }}
          data-testid="public-mobile-book-btn"
        >
          <CalendarBlank size={13} weight="fill" /> Prenota
        </button>
      </div>
      <div className="lg:hidden h-14" aria-hidden />

      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        publicId={publicId}
        propertyTitle={p?.title}
        agencyName={agency.name}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   BrochureMosaic — iter v28 r26
   Mosaico magazine asimmetrico delle foto. Su mobile: griglia 2-col
   semplice. Su desktop: pattern 2-1-1 / 1-1-2 alternato per dare ritmo.
   ════════════════════════════════════════════════════════════════════ */
function BrochureMosaic({ photos, publicId, onOpen }) {
  // Saltiamo la prima foto: è già nella cover hero.
  const items = photos.slice(1);
  if (items.length === 0) return null;

  // Pattern di righe desktop
  const ROW_PATTERNS = [
    ["w-2/3", "w-1/3"],
    ["w-full"],
    ["w-1/3", "w-2/3"],
    ["w-1/2", "w-1/2"],
  ];

  const rows = [];
  let i = 0;
  let pi = 0;
  while (i < items.length) {
    const pattern = ROW_PATTERNS[pi % ROW_PATTERNS.length];
    const slice = items.slice(i, i + pattern.length);
    if (slice.length < pattern.length) {
      if (slice.length === 1) rows.push({ photos: slice, widths: ["w-full"] });
      else rows.push({ photos: slice, widths: ["w-1/2", "w-1/2"] });
    } else {
      rows.push({ photos: slice, widths: pattern });
    }
    i += pattern.length;
    pi += 1;
  }

  return (
    <>
      {/* Desktop: mosaico asimmetrico */}
      <div className="mt-7 hidden sm:block" data-testid="brochure-mosaic-desktop">
        {rows.map((row, ri) => (
          <div key={ri} className="flex gap-2 mb-2">
            {row.photos.map((ph, idx) => (
              <button
                key={ph.id}
                onClick={() => onOpen(photos.indexOf(ph))}
                className={`${row.widths[idx]} aspect-[4/3] overflow-hidden bg-[#F1EEE6] relative group`}
                data-testid={`mosaic-photo-${ph.id}`}
              >
                <img
                  src={publicFileUrl(publicId, ph.url)}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                {ph.kind === "floor_plan" && (
                  <span className="absolute top-2 left-2 bg-[#1C1C1A]/85 text-white text-[9px] tracking-widest uppercase px-2 py-0.5">
                    Planimetria
                  </span>
                )}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Mobile: griglia 2-col compatta */}
      <div className="mt-6 sm:hidden grid grid-cols-2 gap-1.5" data-testid="brochure-mosaic-mobile">
        {items.map((ph) => (
          <button
            key={ph.id}
            onClick={() => onOpen(photos.indexOf(ph))}
            className="aspect-[4/3] overflow-hidden bg-[#F1EEE6] relative"
            data-testid={`mosaic-photo-mobile-${ph.id}`}
          >
            <img src={publicFileUrl(publicId, ph.url)} alt="" className="w-full h-full object-cover" />
            {ph.kind === "floor_plan" && (
              <span className="absolute top-1.5 left-1.5 bg-[#1C1C1A]/85 text-white text-[8px] tracking-widest uppercase px-1.5 py-0.5">
                Planim.
              </span>
            )}
          </button>
        ))}
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════
   BookingModal — invariato (form prenota visita)
   ════════════════════════════════════════════════════════════════════ */
function BookingModal({ open, onClose, publicId, propertyTitle, agencyName }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const days = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 1; i <= 14; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      if (d.getDay() === 0) continue;
      const iso = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });
      out.push({ iso, label });
    }
    return out;
  }, []);

  const TIME_SLOTS = ["10:00", "11:30", "15:00", "16:30", "18:00"];

  if (!open) return null;

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = name.trim().length >= 2 && validEmail && day && time && !submitting;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const requested_at = `${day}T${time}:00`;
      await publicApi.post(`/listing/${publicId}/book-viewing`, {
        contact_name: name.trim(),
        contact_email: email.trim(),
        contact_phone: phone.trim() || null,
        requested_at,
        notes: notes.trim() || null,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore invio richiesta. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" data-testid="public-booking-modal">
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#E5E7E0] bg-gradient-to-br from-[#F1F4ED] to-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Prenota una visita</div>
            <div className="text-[15px] font-semibold text-[#1C1C1A] mt-0.5 truncate max-w-[80vw] sm:max-w-[300px]">{propertyTitle || "Immobile"}</div>
            {agencyName && <div className="text-[11px] text-[#9A968B] mt-0.5">con {agencyName}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 text-[#9A968B] hover:text-[#1C1C1A]" data-testid="public-booking-close" aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {done ? (
            <div className="py-6 text-center" data-testid="public-booking-success">
              <div className="w-14 h-14 rounded-full bg-[#7C8B6E] flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={28} weight="fill" color="#fff" />
              </div>
              <div className="text-[15px] font-semibold text-[#1C1C1A] mb-1">Richiesta inviata!</div>
              <p className="text-[12.5px] text-[#6B6A65] leading-snug max-w-xs mx-auto">
                {agencyName || "L'agente"} la richiamerà al numero <strong className="text-[#1C1C1A]">{phone || email}</strong> per confermare la visita.
              </p>
              <button
                onClick={onClose}
                className="mt-5 px-4 py-2 text-[12.5px] font-semibold bg-[#1C1C1A] text-white rounded-md hover:bg-black"
                data-testid="public-booking-done-close"
              >
                Chiudi
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3.5">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Nome e cognome</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                  placeholder="Luca Ferrari"
                  required
                  data-testid="public-booking-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                    placeholder="mario@email.it"
                    required
                    data-testid="public-booking-email"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-[#E5E7E0] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                    placeholder="+39 333 …"
                    data-testid="public-booking-phone"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-2">Quando preferisci?</label>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="public-booking-days">
                  {days.map((d) => (
                    <button
                      type="button"
                      key={d.iso}
                      onClick={() => setDay(d.iso)}
                      className={`shrink-0 px-3 py-2 text-[12px] rounded-md border transition-all whitespace-nowrap ${day === d.iso ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                      data-testid={`public-booking-day-${d.iso}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap mt-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setTime(t)}
                      disabled={!day}
                      className={`px-3 py-1.5 text-[12px] rounded-full border transition-all ${time === t ? "bg-[#7C8B6E] text-white border-[#7C8B6E]" : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E] disabled:opacity-50 disabled:cursor-not-allowed"}`}
                      data-testid={`public-booking-time-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[10.5px] text-[#9A968B]">L'agente confermerà l'orario esatto via telefono o WhatsApp.</p>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1">Messaggio <span className="text-[#9A968B] font-normal normal-case">(facoltativo)</span></label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-[#E5E7E0] rounded-md text-[13px] focus:outline-none focus:border-[#7C8B6E] resize-none"
                  placeholder="Vorrei vedere anche la cantina…"
                  data-testid="public-booking-notes"
                />
              </div>
              {error && (
                <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="public-booking-error">{error}</div>
              )}
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#7C8B6E] hover:bg-[#6E7D60] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-md transition-colors"
                data-testid="public-booking-submit"
              >
                {submitting ? <Spinner size={15} className="animate-spin" /> : <CalendarBlank size={15} weight="fill" />}
                {submitting ? "Invio…" : "Invia richiesta"}
              </button>
              <p className="text-[10.5px] text-[#9A968B] text-center leading-snug">
                Richiesta non vincolante. {agencyName || "L'agente"} la richiamerà per confermare.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   PropertyDataSheet — scheda tecnica brochure (compatta su 2 colonne)
   ════════════════════════════════════════════════════════════════════ */
function PropertyDataSheet({ p }) {
  if (!p) return null;

  const fmtBool = (v) => v === true ? "Sì" : v === false ? "No" : v;
  const fmtMq = (v) => (v && Number(v) > 0) ? `${v} m²` : (v === true ? "Sì" : v);
  const fmtEur = (v) => (v != null && v !== "" && !Number.isNaN(Number(v))) ? `€ ${Number(v).toLocaleString("it-IT")}/mese` : v;
  const fmtYear = (v) => (v && Number(v) > 1500) ? String(v) : v;

  const rows = [
    { key: "property_type",      label: "Tipologia",         value: p.property_type,                   Icon: House },
    { key: "surface",            label: "Superficie",        value: p.surface ? `${p.surface} m²` : null, Icon: Ruler },
    { key: "rooms",              label: "Locali",            value: p.rooms ? `${p.rooms} locali` : null, Icon: House },
    { key: "bathrooms",          label: "Bagni",             value: p.bathrooms,                        Icon: Bathtub },
    { key: "floor",              label: "Piano",             value: p.floor,                            Icon: Stairs },
    { key: "exposure",           label: "Esposizione",       value: p.exposure,                         Icon: Sun },
    { key: "condition",          label: "Stato",             value: p.condition,                        Icon: Wrench },
    { key: "year_built",         label: "Anno costruzione",  value: fmtYear(p.year_built),              Icon: Calendar },
    { key: "furnished",          label: "Arredamento",       value: fmtBool(p.furnished),               Icon: Couch },
    { key: "balconies",          label: "Balconi",           value: p.balconies,                        Icon: House },
    { key: "terraces",           label: "Terrazzo",          value: fmtMq(p.terraces),                  Icon: House },
    { key: "garden",             label: "Giardino",          value: fmtMq(p.garden),                    Icon: Tree },
    { key: "garage",             label: "Box auto",          value: fmtBool(p.garage),                  Icon: Car },
    { key: "parking",            label: "Posto auto",        value: fmtBool(p.parking),                 Icon: Car },
    { key: "elevator",           label: "Ascensore",         value: fmtBool(p.elevator),                Icon: Elevator },
    { key: "energy_class",       label: "Classe energetica", value: p.energy_class,                     Icon: Lightning },
    { key: "energy_consumption", label: "Consumo energ.",    value: p.energy_consumption,               Icon: Lightning },
    { key: "heating",            label: "Riscaldamento",     value: p.heating,                          Icon: Thermometer },
    { key: "condo_fees",         label: "Spese condominio",  value: fmtEur(p.condo_fees),               Icon: CurrencyEur },
    { key: "cadastral_category", label: "Categoria cat.",    value: p.cadastral_category,               Icon: CurrencyEur },
    { key: "cadastral_income",   label: "Rendita catastale", value: p.cadastral_income != null ? `€ ${Number(p.cadastral_income).toLocaleString("it-IT")}` : null, Icon: CurrencyEur },
    { key: "availability",       label: "Disponibilità",     value: p.availability,                     Icon: Calendar },
  ].filter((r) => r.value !== null && r.value !== undefined && r.value !== "");

  if (rows.length < 3) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 max-w-3xl mx-auto" data-testid="public-data-sheet">
      {rows.map(({ key, label, value, Icon }) => (
        <div key={key} className="flex items-center justify-between gap-3 py-2.5 border-b border-dashed border-[#D8CFB8]" data-testid={`prop-${key}`}>
          <span className="flex items-center gap-2 text-[12px] text-[#6B6A65] min-w-0">
            {Icon && (
              <span className="w-6 h-6 rounded-full bg-[#F1F4ED] text-[#7C8B6E] flex items-center justify-center flex-shrink-0">
                <Icon size={12} weight="bold" />
              </span>
            )}
            <span className="truncate">{label}</span>
          </span>
          <span className="text-[13px] text-[#1C1C1A] font-medium text-right">{value}</span>
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   LightboxView — invariato
   ════════════════════════════════════════════════════════════════════ */
function LightboxView({ photos, startIndex, publicId, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const touchStartX = useRef(null);
  const next = () => setIdx((i) => (i + 1) % photos.length);
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (Math.abs(dx) > 50) { if (dx < 0) next(); else prev(); }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center select-none"
      data-testid="lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="lightbox-close"
        aria-label="Chiudi"
      >
        <X size={28} />
      </button>
      {photos.length > 1 && (
        <button
          onClick={prev}
          className="absolute left-3 sm:left-5 text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="lightbox-prev"
          aria-label="Foto precedente"
        >
          <CaretLeft size={36} />
        </button>
      )}
      <img
        src={publicFileUrl(publicId, photos[idx].url)}
        alt=""
        className="max-h-[88vh] max-w-[92vw] object-contain"
        draggable={false}
      />
      {photos.length > 1 && (
        <button
          onClick={next}
          className="absolute right-3 sm:right-5 text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="lightbox-next"
          aria-label="Foto successiva"
        >
          <CaretRight size={36} />
        </button>
      )}
      {photos.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm" data-testid="lightbox-counter">
          {idx + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   PublicBeforeAfter — slider Prima/Dopo
   iter v28 r26 — etichette ridotte e più discrete (Bodoni italic)
   ════════════════════════════════════════════════════════════════════ */
function PublicBeforeAfter({ beforeUrl, afterUrl, autoLoop = false, delayMs = 0, initialPos = 50, beforeLabel = "Prima", afterLabel = "Dopo" }) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(initialPos);
  const [dragging, setDragging] = useState(false);
  const [active, setActive] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  // iter v28 r27 — Auto-loop continuo: ping-pong tra 20 e 80% finché
  // l'utente non interagisce. Si avvia quando il componente entra in vista.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e?.isIntersecting) setActive(true);
      else setActive(false);
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!autoLoop || !active || userInteracted || dragging) return;
    // Ping-pong continuo. Aspetta delayMs prima del primo movimento per
    // sfasare più slider sulla stessa pagina.
    let t = 0;
    const startTimer = setTimeout(() => {
      // Anima manualmente con setInterval (no CSS keyframe per evitare conflitti).
      let direction = 1;
      let cur = 50;
      t = setInterval(() => {
        cur += direction * 1.2;
        if (cur >= 78) { cur = 78; direction = -1; }
        else if (cur <= 22) { cur = 22; direction = 1; }
        setPos(cur);
      }, 60);
    }, delayMs);
    return () => {
      clearTimeout(startTimer);
      if (t) clearInterval(t);
    };
  }, [autoLoop, active, userInteracted, dragging, delayMs]);

  const updateFromClientX = (clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    setPos((x / rect.width) * 100);
  };

  const onMouseDown = (e) => { setDragging(true); setUserInteracted(true); updateFromClientX(e.clientX); };
  const onMouseMove = (e) => { if (dragging) updateFromClientX(e.clientX); };
  const onMouseUp = () => setDragging(false);
  const onTouchStart = (e) => { setDragging(true); setUserInteracted(true); updateFromClientX(e.touches[0].clientX); };
  const onTouchMove = (e) => { if (dragging) updateFromClientX(e.touches[0].clientX); };
  const onTouchEnd = () => setDragging(false);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  return (
    <div
      ref={wrapRef}
      className="relative aspect-[4/3] overflow-hidden bg-[#1C1C1A] select-none cursor-ew-resize"
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-testid="public-before-after"
    >
      <img
        src={afterUrl}
        alt="Dopo"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />
      <img
        src={beforeUrl}
        alt="Prima"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)`, transition: dragging || autoLoop ? "none" : "clip-path 700ms cubic-bezier(0.22, 1, 0.36, 1)" }}
        draggable={false}
      />
      {/* Etichette Bodoni italic, più piccole */}
      <span className="absolute bottom-2 left-2 font-editorial italic text-white/95 text-[12px] tracking-wide pointer-events-none drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
        {beforeLabel}
      </span>
      <span className="absolute bottom-2 right-2 font-editorial italic text-white/95 text-[12px] tracking-wide pointer-events-none drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
        {afterLabel}
      </span>
      <div
        className="absolute top-0 bottom-0 w-[1.5px] bg-white/85 shadow-[0_0_18px_rgba(0,0,0,0.4)] pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-0.75px)", transition: dragging || autoLoop ? "none" : "left 700ms cubic-bezier(0.22, 1, 0.36, 1)" }}
      />
      <div
        className="absolute top-1/2 w-9 h-9 -mt-[18px] bg-white rounded-full shadow-[0_4px_14px_rgba(0,0,0,0.35)] flex items-center justify-center pointer-events-none"
        style={{ left: `calc(${pos}% - 18px)`, transition: dragging || autoLoop ? "none" : "left 700ms cubic-bezier(0.22, 1, 0.36, 1)" }}
      >
        <CaretLeft size={12} weight="bold" className="text-[#1C1C1A]" />
        <CaretRight size={12} weight="bold" className="text-[#1C1C1A] -ml-0.5" />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   CoverSlideshow — iter v28 r27
   Slideshow auto-rotating in copertina con crossfade + tint brand color
   (mix-blend-mode multiply per dare l'effetto volantino stampato).
   ════════════════════════════════════════════════════════════════════ */
function CoverSlideshow({ photos, publicId, brandColor }) {
  const [idx, setIdx] = useState(0);
  const list = photos.length > 0 ? photos : [null];

  useEffect(() => {
    if (list.length <= 1) return;
    const t = setInterval(() => setIdx((c) => (c + 1) % list.length), 5000);
    return () => clearInterval(t);
  }, [list.length]);

  if (list[0] === null) {
    // No photos: fallback solo brand color
    return <div className="absolute inset-0" style={{ background: brandColor }} />;
  }

  return (
    <div className="absolute inset-0 overflow-hidden">
      {list.map((ph, i) => (
        <img
          key={ph.id || i}
          src={publicFileUrl(publicId, ph.url)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms]"
          style={{ opacity: i === idx ? 1 : 0 }}
          draggable={false}
        />
      ))}
      {/* Tint brand color multiply su tutta la foto */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: brandColor, mixBlendMode: "multiply", opacity: 0.78 }}
      />
      {/* Velo scuro leggero per leggibilità testo bianco */}
      <div className="absolute inset-0 bg-black/15 pointer-events-none" />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   CoverSection — iter v28 r44
   Renderizza la PRIMA pagina (copertina) della brochure pubblica con
   layout STRUTTURALMENTE diverso in base a `variant`. Non solo font o
   colori: posizione foto, struttura blocchi, gerarchia visiva cambia.
   ════════════════════════════════════════════════════════════════════ */
function CoverSection({ variant, p, agency, photos, publicId, brandColor, pageIdx, totalPages }) {
  const coverPhoto = photos.find((ph) => !ph._is_before) || photos[0];
  const coverUrl = coverPhoto ? publicFileUrl(publicId, coverPhoto.url) : null;
  const pageNum = `${pageIdx("cover")} / ${String(totalPages).padStart(2, "0")}`;
  const isMonochrome = agency?.logo_is_monochrome !== false;
  const priceLabel = formatPrice(p?.price) || "Trattativa riservata";
  const typeLabel = p?.listing_type === "affitto" ? "In affitto" : (p?.property_type ? "In vendita" : "Esclusiva");
  const websiteShort = agency?.website ? agency.website.replace(/^https?:\/\//, "").replace(/\/$/, "") : "";

  // ──────────── LUXURY ──────────── foto incorniciata + cornice doppia
  if (variant === "luxury") {
    return (
      <section className="brochure-page brochure-frame relative overflow-hidden" data-testid="public-hero-section" style={{ padding: "32px 24px 40px", background: "#FBF4E1", minHeight: "92vh" }}>
        <span className="brochure-pagenum">{pageNum}</span>
        <div className="max-w-2xl mx-auto text-center">
          {/* Logo agenzia */}
          {agency?.logo_url ? (
            <img src={agency.logo_url} alt={agency.name || ""}
              className="h-10 sm:h-12 w-auto max-w-[200px] mx-auto object-contain" data-testid="public-brand-logo" />
          ) : (
            <div className="font-editorial italic text-xl sm:text-2xl tracking-[0.18em] uppercase text-[#2A1F0E]" data-testid="public-brand">
              {agency?.name}
            </div>
          )}
          {websiteShort && (
            <div className="mt-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.32em] text-[#B08A2D] font-semibold">
              Real Estate · {agency?.listing_type === "affitto" ? "Locazione" : "Esclusiva"}
            </div>
          )}
          {/* Linea oro */}
          <div className="mt-4 mb-5 w-16 h-px bg-[#D4B873] mx-auto" />
          {/* Label tipologia */}
          <div className="text-[10px] uppercase tracking-[0.36em] font-semibold text-[#B08A2D]" data-testid="public-type">
            {typeLabel}
          </div>
          {/* Titolo italic sopra la foto */}
          <h1 className="mt-3 font-editorial italic text-[clamp(26px,5.5vw,42px)] text-[#2A1F0E] leading-[1.05]" data-testid="public-title">
            {p?.title}
          </h1>
          {/* Foto incorniciata */}
          {coverUrl && (
            <div className="mt-5 mx-auto" style={{ maxWidth: 440 }}>
              <div className="relative" style={{ padding: 8, background: "#FFFFFF", border: "1px solid #D4B873", boxShadow: "0 6px 16px -8px rgba(196,154,42,0.35)" }}>
                <img src={coverUrl} alt="" className="w-full aspect-[4/3] object-cover" draggable={false} />
              </div>
            </div>
          )}
          {/* Caption descrittiva */}
          {(p?.city || p?.address) && (
            <div className="mt-5 font-editorial italic text-[12px] sm:text-[13px] text-[#6B5B3A] tracking-[0.18em] uppercase">
              {p.address ? `${p.address} · ` : ""}{p.city}
            </div>
          )}
          {/* Linea oro */}
          <div className="mt-4 w-16 h-px bg-[#D4B873] mx-auto" />
          {/* Prezzo serif italic gold */}
          <div className="mt-4 font-editorial text-[clamp(24px,5vw,38px)] text-[#B08A2D]" data-testid="public-price">
            {priceLabel}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.32em] font-semibold text-[#6B5B3A]">
            {p?.surface && <span>{p.surface} MQ</span>}
            {p?.rooms && <span> · {p.rooms} LOCALI</span>}
            {p?.bathrooms && <span> · {p.bathrooms} BAGNI</span>}
          </div>
        </div>
      </section>
    );
  }

  // ──────────── MODERNO ──────────── foto top-half + pannello brand bottom-half
  if (variant === "moderno") {
    return (
      <section className="brochure-page brochure-frame relative overflow-hidden" data-testid="public-hero-section" style={{ padding: 0, background: "#FFFFFF", minHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <span className="brochure-pagenum" style={{ color: "#9A968B", zIndex: 20 }}>{pageNum}</span>
        {/* Foto top half */}
        <div className="relative flex-1" style={{ minHeight: "42vh" }}>
          {coverUrl ? (
            <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
          ) : <div className="absolute inset-0 bg-[#F1F4ED]" />}
          {/* Badge "NUOVO" / logo agenzia */}
          <div className="absolute top-4 left-4 inline-flex items-center gap-2">
            <span className="px-2.5 py-1 text-[9px] uppercase tracking-[0.24em] font-bold text-white" style={{ background: brandColor }}>
              {p?.listing_type === "affitto" ? "Affitto" : "Nuovo"}
            </span>
            {agency?.logo_url && (
              <img src={agency.logo_url} alt="" className="h-6 sm:h-7 w-auto max-w-[120px] object-contain"
                style={isMonochrome ? { filter: "brightness(0) invert(1)" } : undefined} data-testid="public-brand-logo" />
            )}
          </div>
        </div>
        {/* Pannello pieno brand color bottom half */}
        <div className="relative flex-1 px-6 sm:px-10 py-7 text-white flex flex-col justify-center" style={{ background: brandColor, minHeight: "42vh" }}>
          <div className="text-[10px] uppercase tracking-[0.32em] font-bold opacity-90" data-testid="public-type">
            {typeLabel}
          </div>
          <h1 className="mt-2 font-bold text-[clamp(28px,6vw,50px)] leading-[1.05] tracking-[-0.025em] uppercase" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="public-title">
            {p?.title}
          </h1>
          {(p?.city || p?.address) && (
            <div className="mt-2 text-[12px] sm:text-[13px] opacity-90">
              {p.address ? `${p.address} · ` : ""}{p.city}
            </div>
          )}
          <div className="mt-5 w-12 h-px bg-white/45" />
          <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
            <div className="font-bold text-[clamp(24px,5vw,38px)] leading-none tracking-[-0.025em]" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="public-price">
              {priceLabel}
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold opacity-90">
              {p?.surface && <span>{p.surface} MQ</span>}
              {p?.rooms && <span> · {p.rooms} LOC.</span>}
              {p?.bathrooms && <span> · {p.bathrooms} BAGNI</span>}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ──────────── MINIMAL ──────────── foto a destra + colonna info sinistra
  if (variant === "minimal") {
    return (
      <section className="brochure-page brochure-frame relative overflow-hidden" data-testid="public-hero-section" style={{ padding: 0, background: "#FAFAF7", minHeight: "92vh", display: "flex" }}>
        <span className="brochure-pagenum" style={{ color: "#C8C4B7" }}>{pageNum}</span>
        {/* Stripe colore brand laterale sinistra */}
        <div className="hidden sm:block flex-shrink-0" style={{ width: 6, background: brandColor }} />
        {/* Colonna info sinistra */}
        <div className="flex flex-col justify-between px-5 sm:px-8 py-7 flex-shrink-0" style={{ width: "min(46%, 360px)" }}>
          <div>
            {agency?.logo_url ? (
              <img src={agency.logo_url} alt={agency.name || ""} className="h-7 sm:h-9 w-auto max-w-[140px] object-contain" data-testid="public-brand-logo" />
            ) : (
              <div className="font-bold text-[13px] sm:text-[14px] tracking-[0.04em] uppercase text-[#1C1C1A]" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="public-brand">
                {agency?.name}
              </div>
            )}
            {websiteShort && (
              <div className="mt-1 text-[9px] sm:text-[10px] uppercase tracking-[0.24em] text-[#9A968B] font-medium">
                {websiteShort}
              </div>
            )}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.32em] text-[#9A968B] font-medium" data-testid="public-type">
              {p?.property_type || "Immobile"} · {p?.listing_type === "affitto" ? "Locazione" : "Vendita"}
            </div>
            <h1 className="mt-2 font-light text-[clamp(28px,6vw,46px)] text-[#1C1C1A] leading-[1.05] tracking-[-0.02em]" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="public-title">
              {p?.city ? `${p.city},` : ""}<br />
              <span className="font-bold">{(p?.title || "immobile").toLowerCase().split(" ")[0]}.</span>
            </h1>
            {p?.address && (
              <div className="mt-2 text-[11px] text-[#6B6A65] font-light">{p.address}</div>
            )}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.28em] text-[#9A968B] font-medium mb-1">
              {typeLabel}
            </div>
            <div className="flex items-baseline gap-1" data-testid="public-price">
              <span className="text-[12px] text-[#1C1C1A] font-light">€</span>
              <span className="text-[clamp(22px,4.5vw,32px)] font-bold text-[#1C1C1A] tracking-[-0.025em]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {p?.price ? Number(p.price).toLocaleString("it-IT") : "—"}
              </span>
            </div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.24em] text-[#6B6A65] font-medium">
              {p?.surface && <span>{p.surface} mq</span>}
              {p?.rooms && <span> · {p.rooms} locali</span>}
            </div>
          </div>
        </div>
        {/* Foto destra full-bleed */}
        <div className="relative flex-1">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
          ) : <div className="absolute inset-0 bg-[#F1F4ED]" />}
        </div>
      </section>
    );
  }

  // ──────────── ELEGANTE (default) ──────────── foto blended + titolo italic centrato
  return (
    <section
      className="brochure-page brochure-frame relative overflow-hidden text-white"
      style={{ padding: 0, background: brandColor }}
      data-testid="public-hero-section"
    >
      <span className="brochure-pagenum z-20 text-white/70" style={{ color: "rgba(255,255,255,0.7)" }}>{pageNum}</span>
      <div className="relative min-h-[88vh] sm:min-h-[92vh] flex flex-col justify-between p-6 sm:p-10 lg:p-14">
        <CoverSlideshow photos={photos.slice(0, 8)} publicId={publicId} brandColor={brandColor} />
        <div className="pointer-events-none absolute inset-[14px] sm:inset-5 border border-white/30 z-[1]" />
        <div className="relative z-10 text-center pt-4 sm:pt-6">
          {agency?.logo_url ? (
            <img src={agency.logo_url} alt={agency.name || "Agenzia"}
              className="h-12 sm:h-16 w-auto max-w-[260px] mx-auto object-contain"
              style={isMonochrome ? { filter: "brightness(0) invert(1)" } : undefined}
              data-testid="public-brand-logo" />
          ) : agency?.name ? (
            <div className="font-editorial italic text-2xl sm:text-3xl tracking-[0.05em] uppercase" data-testid="public-brand">
              {agency.name}
            </div>
          ) : null}
          {websiteShort && (
            <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.28em] opacity-75">{websiteShort}</div>
          )}
          <div className="mt-3 sm:mt-4 w-12 h-px bg-white/45 mx-auto" />
        </div>
        <div className="relative z-10 text-center max-w-2xl mx-auto -mt-4 sm:-mt-8">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-semibold opacity-90 mb-4 sm:mb-5" data-testid="public-type">
            {typeLabel}{p?.property_type ? ` · ${p.property_type}` : ""}
          </div>
          <h1 className="font-editorial italic text-[clamp(34px,8vw,68px)] leading-[1.04] tracking-tight" data-testid="public-title">
            {p?.title}
          </h1>
          {(p?.city || p?.address) && (
            <div className="mt-3 sm:mt-4 font-editorial italic text-base sm:text-lg opacity-90">
              {p.address ? `${p.address} · ` : ""}{p.city}
            </div>
          )}
        </div>
        <div className="relative z-10 text-center">
          <div className="font-editorial text-[clamp(28px,6vw,46px)] leading-none" data-testid="public-price">
            {priceLabel}
          </div>
          <div className="mt-2 sm:mt-3 flex items-center justify-center gap-x-3 gap-y-1 flex-wrap text-[10px] sm:text-[11px] uppercase tracking-[0.24em] font-semibold opacity-90">
            {p?.surface && <span>{p.surface} mq</span>}
            {p?.rooms && <><span className="opacity-50">·</span><span>{p.rooms} loc.</span></>}
            {p?.bathrooms && <><span className="opacity-50">·</span><span>{p.bathrooms} bagni</span></>}
            {p?.energy_class && <><span className="opacity-50">·</span><span>Classe {p.energy_class}</span></>}
          </div>
          <div className="mt-6 sm:mt-7 w-12 h-px bg-white/45 mx-auto" />
          <div className="mt-3 text-[12px] sm:text-[13px] opacity-90 font-medium">
            {agency.agent_name || agency.name}
            {agency.phone && <span className="opacity-70"> · {agency.phone}</span>}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   ListingSummaryCard — iter v28 r49
   Scheda riassuntiva in stile Immobiliare.it/Idealista: subito dopo la
   cover artistica, mostra in una sola card moderna:
   - Foto principale grande (cliccabile → lightbox) + 3 thumbnails (la 4ª
     "vedi tutte +N" se ci sono più foto)
   - Prezzo grande bold + logo agenzia destra
   - Titolo + indirizzo (città, via, quartiere)
   - Chips specs: locali, m², bagni, piano, classe energetica
   - Bottone "Contatta" che apre il booking modal
   ════════════════════════════════════════════════════════════════════ */
function ListingSummaryCard({ p, agency, photos, publicId, brandColor, onContactClick, onGalleryClick }) {
  const [mainIdx, setMainIdx] = useState(0);
  const cleanPhotos = photos.filter((ph) => !ph._is_before);
  if (cleanPhotos.length === 0) return null;

  const mainPhoto = cleanPhotos[mainIdx] || cleanPhotos[0];
  const thumbs = cleanPhotos.slice(0, 4);
  const extraCount = Math.max(0, cleanPhotos.length - 4);
  const priceLabel = formatPrice(p?.price) || "Trattativa riservata";
  const typeLabel = p?.listing_type === "affitto" ? "In affitto" : "In vendita";
  const isMonochrome = agency?.logo_is_monochrome !== false;
  const addressLine = [p?.address, p?.neighborhood, p?.city].filter(Boolean).join(", ");

  const specs = [
    p?.rooms && { v: `${p.rooms} ${p.rooms === 1 ? "locale" : "locali"}`, k: "rooms" },
    p?.surface && { v: `${p.surface} m²`, k: "surface" },
    p?.bathrooms && { v: `${p.bathrooms} ${p.bathrooms === 1 ? "bagno" : "bagni"}`, k: "bath" },
    p?.floor && { v: `Piano ${p.floor}`, k: "floor" },
    p?.energy_class && { v: `Classe ${p.energy_class}`, k: "energy" },
  ].filter(Boolean);

  return (
    <section
      className="max-w-[920px] mx-auto bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] rounded-2xl overflow-hidden my-5 mx-3 sm:mx-auto"
      data-testid="public-summary-card"
    >
      {/* Foto principale + counter + tipologia chip */}
      <div className="relative bg-[#1C1C1A]">
        <button
          type="button"
          onClick={onGalleryClick}
          className="block w-full aspect-[4/3] sm:aspect-[16/9] overflow-hidden"
          data-testid="summary-main-photo"
        >
          <img
            src={publicFileUrl(publicId, mainPhoto.url)}
            alt=""
            className="w-full h-full object-cover transition-transform hover:scale-[1.01]"
            draggable={false}
          />
        </button>
        {/* Chip tipologia top-left */}
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-white rounded"
          style={{ background: brandColor }}
        >
          {typeLabel}
        </span>
        {/* Counter foto bottom-right */}
        <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-white bg-black/65 backdrop-blur-sm rounded">
          {mainIdx + 1} / {cleanPhotos.length}
        </div>
      </div>

      {/* Strip thumbs (visibile su tablet+, scroll orizzontale su mobile) */}
      {thumbs.length > 1 && (
        <div className="px-3 pt-3 flex gap-2 overflow-x-auto" data-testid="summary-thumbs">
          {thumbs.map((ph, i) => {
            const isLast = i === thumbs.length - 1 && extraCount > 0;
            return (
              <button
                key={ph.id || i}
                type="button"
                onClick={() => (isLast ? onGalleryClick() : setMainIdx(i))}
                className={`relative flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                  i === mainIdx && !isLast ? "border-[#1C1C1A]" : "border-transparent hover:border-[#9A968B]"
                }`}
                style={{ width: 92, height: 70 }}
                data-testid={`summary-thumb-${i}`}
              >
                <img
                  src={publicFileUrl(publicId, ph.url)}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                {isLast && (
                  <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                    <span className="text-white text-[12px] font-semibold">+{extraCount} foto</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* INFO area */}
      <div className="p-4 sm:p-6">
        {/* Riga prezzo + logo agenzia */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div
              className="text-[clamp(24px,4.5vw,32px)] font-bold tracking-[-0.025em] leading-none"
              style={{ color: "#1C1C1A", fontFamily: "'Manrope', sans-serif" }}
              data-testid="summary-price"
            >
              {priceLabel}
            </div>
          </div>
          {(agency?.logo_url || agency?.name) && (
            <div className="flex-shrink-0 text-right" data-testid="summary-agency">
              {agency.logo_url ? (
                <img
                  src={agency.logo_url}
                  alt={agency.name || ""}
                  className="h-7 sm:h-9 w-auto max-w-[110px] object-contain ml-auto"
                  style={isMonochrome ? { filter: "brightness(0.2)" } : undefined}
                />
              ) : (
                <div className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.1em] text-[#1C1C1A]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {agency.name}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Titolo + indirizzo */}
        {p?.title && (
          <h1
            className="mt-2.5 text-[clamp(18px,3vw,22px)] font-semibold leading-tight tracking-[-0.015em] text-[#1C1C1A]"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="summary-title"
          >
            {p.title}
          </h1>
        )}
        {addressLine && (
          <div className="mt-1 text-[13px] sm:text-[14px] text-[#6B6A65] leading-snug" data-testid="summary-address">
            {addressLine}
          </div>
        )}

        {/* Specs in chips moderne */}
        {specs.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-y-2.5 gap-x-4" data-testid="summary-specs">
            {specs.map(({ v, k }) => (
              <div key={k} className="flex items-center gap-2 text-[13px] sm:text-[14px] text-[#1C1C1A]">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: brandColor }} />
                <span className="leading-snug">{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* CTA contatta */}
        <button
          type="button"
          onClick={onContactClick}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-white text-[14px] font-semibold rounded-xl transition-opacity hover:opacity-90"
          style={{ background: brandColor, fontFamily: "'Manrope', sans-serif" }}
          data-testid="summary-cta-contact"
        >
          Contatta l'agenzia · Prenota visita
        </button>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   BrochurePageHeader — iter v28 r27
   Mini-header che appare in alto di ogni pagina brochure (non-cover).
   Mostra il logo agenzia o il nome, centrato, in piccolo, come header
   ricorrente di un volantino stampato multi-pagina.
   ════════════════════════════════════════════════════════════════════ */
function BrochurePageHeader({ agency, dark = false }) {
  if (!agency?.logo_url && !agency?.name) return null;
  // iter v28 r35 — Niente pill di sfondo. Solo logo monocromatico viene
  // invertito su pagina dark; policromo resta a colori naturali.
  // iter v28 r44 — Logo agenzia rimpicciolito (richiesta utente) per dare
  // più respiro alle pagine interne 2+.
  const isMonochrome = agency?.logo_is_monochrome !== false;
  return (
    <div className="text-center mb-4 pb-3 border-b border-dashed" style={{ borderColor: dark ? "rgba(154,142,114,0.35)" : "#E8DFCC" }}>
      {agency?.logo_url ? (
        <img
          src={agency.logo_url}
          alt={agency.name || ""}
          className="h-6 sm:h-7 w-auto max-w-[140px] mx-auto object-contain"
          style={dark && isMonochrome ? { filter: "brightness(0) invert(1)" } : undefined}
        />
      ) : (
        <div
          className={`font-editorial italic text-[12px] sm:text-[13px] tracking-[0.05em] uppercase ${dark ? "text-[#FBF8F1]" : "text-[#1C1C1A]"}`}
        >
          {agency.name}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   GallerySlider — iter v28 r28
   Foto principale grande + thumbnail strip cliccabile.
   Click thumbnail = cambia foto principale (con fade).
   Click foto principale = apre lightbox fullscreen.
   ════════════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════════
   GalleryCollage — iter v28 r36
   Collage magazine asimmetrico, "Pinterest-like" ma più ordinato.
   Pattern di righe: alterna combinazioni "2-up" (50/50), "big+small"
   (2/3 + 1/3), e "small+big" (1/3 + 2/3). Su mobile diventa 2 colonne.
   Click su qualsiasi foto = apre lightbox a tutto schermo.
   ════════════════════════════════════════════════════════════════════ */
function GalleryCollage({ photos, publicId, onZoom }) {
  if (photos.length === 0) return null;

  // Pattern asimmetrici desktop (somma 100%, ogni cella aspect 4:3)
  const PATTERNS = [
    [50, 50],          // 2-up
    [66.67, 33.33],    // big + small
    [33.33, 66.67],    // small + big
    [100],             // full bleed singolo (per dare respiro)
    [50, 50],
  ];

  // Distribuiamo le foto sulle righe seguendo i pattern
  const rows = [];
  let i = 0;
  let pi = 0;
  while (i < photos.length) {
    const pattern = PATTERNS[pi % PATTERNS.length];
    const slice = photos.slice(i, i + pattern.length);
    if (slice.length < pattern.length) {
      // Riga finale incompleta: forza 50/50 o full
      if (slice.length === 1) rows.push({ photos: slice, widths: [100] });
      else rows.push({ photos: slice, widths: [50, 50] });
    } else {
      rows.push({ photos: slice, widths: pattern });
    }
    i += pattern.length;
    pi += 1;
  }

  return (
    <>
      {/* Desktop / tablet: layout asimmetrico */}
      <div className="hidden sm:block max-w-3xl mx-auto" data-testid="gallery-collage-desktop">
        {rows.map((row, ri) => (
          <div key={ri} className="flex gap-2 mb-2 last:mb-0">
            {row.photos.map((ph, idx) => {
              const globalIdx = photos.indexOf(ph);
              return (
                <button
                  key={ph.id}
                  onClick={() => onZoom(globalIdx)}
                  className="aspect-[4/3] overflow-hidden bg-[#F1EEE6] relative group transition-all"
                  style={{ flex: `0 0 calc(${row.widths[idx]}% - ${(row.widths.length - 1) * 4 / row.widths.length}px)` }}
                  data-testid={`collage-photo-${ph.id}`}
                  aria-label={`Foto ${globalIdx + 1}`}
                >
                  <img
                    src={publicFileUrl(publicId, ph.url)}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  {/* Pillola "Prima" su foto originali in sequenza */}
                  {ph._is_before && (
                    <span className="absolute top-2 left-2 bg-white/95 text-[#1C1C1A] text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full font-semibold font-editorial italic">
                      Prima
                    </span>
                  )}
                  {/* Indicatore zoom on-hover */}
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 text-[#1C1C1A] text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full font-medium">
                      Ingrandisci
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Mobile: griglia 2-col regolare per chiarezza */}
      <div className="sm:hidden grid grid-cols-2 gap-1.5" data-testid="gallery-collage-mobile">
        {photos.map((ph, idx) => (
          <button
            key={ph.id}
            onClick={() => onZoom(idx)}
            className="aspect-[4/3] overflow-hidden bg-[#F1EEE6] relative"
            data-testid={`collage-photo-mobile-${ph.id}`}
            aria-label={`Foto ${idx + 1}`}
          >
            <img
              src={publicFileUrl(publicId, ph.url)}
              alt=""
              className="w-full h-full object-cover"
            />
            {ph._is_before && (
              <span className="absolute top-1.5 left-1.5 bg-white/95 text-[#1C1C1A] text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-semibold font-editorial italic">
                Prima
              </span>
            )}
          </button>
        ))}
      </div>
    </>
  );
}


function GallerySlider({ photos, publicId, onZoom }) {
  const [idx, setIdx] = useState(0);
  if (photos.length === 0) return null;
  const current = photos[idx];

  return (
    <div className="max-w-3xl mx-auto" data-testid="gallery-slider">
      {/* Foto principale */}
      <button
        onClick={() => onZoom(idx)}
        className="block w-full aspect-[4/3] bg-[#F1EEE6] overflow-hidden relative group"
        data-testid="gallery-main"
      >
        <img
          key={current.id}
          src={publicFileUrl(publicId, current.url)}
          alt=""
          className="w-full h-full object-cover"
          style={{ animation: "publicFadeIn 400ms ease" }}
        />
        <span className="absolute bottom-3 right-3 bg-black/55 text-white text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          Ingrandisci
        </span>
        <span className="absolute top-3 right-3 bg-black/55 text-white text-[11px] px-2.5 py-1 rounded-full font-medium">
          {idx + 1} / {photos.length}
        </span>
        {/* iter v28 r29 — Pill "Prima" / "Dopo" per la sequenza foto.
            Mostra il badge SOLO quando la foto è in coppia (cioè dopo che
            il toggle "Prima · Dopo" è ON e ha generato la sequenza). */}
        {current._is_before && (
          <span className="absolute top-3 left-3 bg-white/95 text-[#1C1C1A] text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full font-semibold font-editorial italic">
            Prima
          </span>
        )}
      </button>

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div
          className="mt-3 flex gap-2 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          data-testid="gallery-thumbs"
        >
          {photos.map((ph, i) => (
            <button
              key={ph.id}
              onClick={() => setIdx(i)}
              className={`flex-shrink-0 w-20 h-16 sm:w-24 sm:h-20 overflow-hidden bg-[#F1EEE6] transition-all relative ${
                i === idx ? "" : "opacity-65 hover:opacity-100"
              }`}
              style={i === idx ? { boxShadow: "0 0 0 2px #1C1C1A" } : undefined}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Foto ${i + 1}`}
            >
              <img
                src={publicFileUrl(publicId, ph.url)}
                alt=""
                className="w-full h-full object-cover"
              />
              {ph._is_before && (
                <span className="absolute bottom-1 left-1 bg-white/90 text-[#1C1C1A] text-[8px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm font-bold">
                  Prima
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   BeforeAfterSelector — iter v28 r28
   Un solo slider Prima/Dopo grande + thumbnail strip cliccabile sotto.
   ════════════════════════════════════════════════════════════════════ */
function BeforeAfterSelector({ items, publicId }) {
  const [idx, setIdx] = useState(0);
  if (items.length === 0) return null;
  const current = items[idx];

  return (
    <div className="max-w-3xl mx-auto" data-testid="ba-selector">
      <PublicBeforeAfter
        key={current.id}
        beforeUrl={publicFileUrl(publicId, current.original_url)}
        afterUrl={publicFileUrl(publicId, current.staged_url)}
        autoLoop
      />
      {items.length > 1 && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2 justify-center" style={{ scrollbarWidth: "none" }} data-testid="ba-thumbs">
          {items.map((ph, i) => (
            <button
              key={ph.id}
              onClick={() => setIdx(i)}
              className={`flex-shrink-0 w-20 h-16 sm:w-24 sm:h-20 overflow-hidden bg-[#F1EEE6] transition-all relative ${
                i === idx ? "" : "opacity-60 hover:opacity-100"
              }`}
              style={i === idx ? { boxShadow: "0 0 0 2px #1C1C1A" } : undefined}
              data-testid={`ba-thumb-${i}`}
            >
              <img src={publicFileUrl(publicId, ph.staged_url)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   PlanimetriaSection — iter v28 r28
   Coppie vuota+arredata → slider Prima/Dopo con vista iniziale VUOTA.
   Singole → immagine grande centrata.
   ════════════════════════════════════════════════════════════════════ */
function PlanimetriaSection({ pairs, singles, publicId }) {
  return (
    <div className="max-w-2xl mx-auto" data-testid="planimetria-section">
      {/* iter v28 r36 — Per le coppie vuota+arredata, di default mostriamo
          l'ARREDATA (più completa visivamente). User può trascinare per
          vedere la vuota originale. initialPos=0 → after (arredata) piena. */}
      {pairs.map((ph) => (
        <div key={ph.id} className="mb-6 last:mb-0 bg-white border border-[#E8DFCC] p-3 sm:p-4">
          <PublicBeforeAfter
            beforeUrl={publicFileUrl(publicId, ph.original_url)}
            afterUrl={publicFileUrl(publicId, ph.staged_url)}
            initialPos={0}
            beforeLabel="Vuota"
            afterLabel="Arredata"
          />
          <div className="text-[11px] text-[#6B6A65] text-center mt-2 font-light">
            Trascina ← per vedere la pianta vuota
          </div>
        </div>
      ))}
      {singles.map((ph) => (
        <div key={ph.id} className={`bg-white border border-[#E8DFCC] p-3 sm:p-4 ${pairs.length > 0 ? "mt-6" : ""}`}>
          <img
            src={publicFileUrl(publicId, ph.staged_url || ph.url)}
            alt="Planimetria"
            className="w-full max-h-[520px] object-contain mx-auto block"
          />
        </div>
      ))}
    </div>
  );
}

/* Keyframe inline una sola volta (per il fade della galleria) */
if (typeof document !== "undefined" && !document.getElementById("public-listing-keyframes")) {
  const s = document.createElement("style");
  s.id = "public-listing-keyframes";
  s.textContent = "@keyframes publicFadeIn { from { opacity: 0.4 } to { opacity: 1 } }";
  document.head.appendChild(s);
}



